import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Avatar, Tabs, Pagination, Breadcrumb } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DribbbleOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import {
  BookingsType,
  CreateUserByAdminType,
  UserProfileType,
} from '../../../types/types';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getUserByIdRequest,
  updateUserAdminRequest,
} from '../../../api/requests/users';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import UserForm from './UserForm';
import { UserRole } from '../../../enums/enums';
import { getAllBookings } from '../../../api/requests/bookings';
import { USERMANAGEMENTPATH } from '../../../routes/routePaths';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import Spinner from '../../common/Spinner';
import ViewBookingDrawer from '../FacilityBookings/ViewBookingDrawer';

const BOOKINGS_PER_PAGE = 2;
const noBookingMessages: Record<string, string> = {
  upcoming: 'No upcoming bookings',
  completed: 'No completed bookings',
  cancelled: 'No cancelled bookings',
};

const UserDetailsView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState<string>('upcoming');
  const [user, setUser] = useState<UserProfileType>();
  const [loading, setLoading] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [bookings, setBookings] = useState<BookingsType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const scrollRefs = useRef<HTMLDivElement[]>([]);
  const [needsFade, setNeedsFade] = useState<boolean[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );

  function getRoleNameById(id: UserRole): string | undefined {
    return Object.keys(UserRole).find(
      (key) => UserRole[key as keyof typeof UserRole] === id,
    );
  }

  const handleEditUser = () => {
    setEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
  };

  const handleEditSubmit = async (updatedUser: any) => {
    if (!userId) return;
    try {
      setLoading(true);
      const updateData = {
        fullName: updatedUser.fullName?.trim(),
        email: updatedUser.email?.trim(),
        phoneNumber: updatedUser.phoneNumber?.trim(),
        bio: updatedUser.bio?.trim() || '',
        role: updatedUser.userRole,
        profilePictureUrl:
          updatedUser.profilePicture?.file?.originFileObj || '',
        entityStatus: updatedUser.entityStatus,
      } as CreateUserByAdminType;
      const response = await updateUserAdminRequest(Number(userId), updateData);
      if (response?.isSuccess) {
        snackbar.success('User updated successfully');
        setEditModalVisible(false);
        fetchUserDetails(userId);
      }
    } catch (error: any) {
      snackbar.error(error.Error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const getDate = (dateStr: string) => {
    const date = new Date(dateStr);

    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return formatted;
  };

  const getTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const formatted = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return formatted;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedBookings = useMemo(
    () =>
      bookings.slice(
        (currentPage - 1) * BOOKINGS_PER_PAGE,
        currentPage * BOOKINGS_PER_PAGE,
      ),
    [bookings, currentPage],
  );

  const handleReturnToUsersList = () => {
    navigate(`${USERMANAGEMENTPATH}`);
  };

  const checkAndSetFades = () => {
    const newFadeState = scrollRefs.current.map((ref) => {
      if (!ref) return false;
      const isScrollable = ref.scrollWidth > ref.clientWidth;
      const isAtEnd = ref.scrollWidth - ref.scrollLeft <= ref.clientWidth + 1;
      return isScrollable && !isAtEnd;
    });
    setNeedsFade(newFadeState);
  };

  const fetchUserDetails = async (id: string) => {
    try {
      setLoading(true);
      const user = await getUserByIdRequest(Number(id));
      if (user?.isSuccess) setUser(user?.value);
    } catch (error) {
      snackbar.error('Failed to fetch user details');
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBookings = async (userId: number, status: string) => {
    try {
      setBookingsLoading(true);
      const response = await getAllBookings({
        userId: userId,
        showBookingSlots: true,
        status: status === 'upcoming' ? 2 : status === 'completed' ? 4 : 3,
      });
      if (response?.isSuccess && response?.value)
        setBookings(response.value.rows);
    } catch (error) {
      snackbar.error('Failed to fetch booking details');
      console.log('Error fetching booking details', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleOpenDrawer = (bookingId: number) => {
    setOpenDrawer(true);
    setSelectedBookingId(bookingId);
  };
  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedBookingId(null);
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetails(userId);
      getBookings(Number(userId), activeTab);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      getBookings(Number(userId), activeTab);
      setCurrentPage(1);
    }
  }, [activeTab]);

  useEffect(() => {
    checkAndSetFades();

    scrollRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.addEventListener('scroll', checkAndSetFades);
      }
    });

    return () => {
      scrollRefs.current.forEach((ref) => {
        if (ref) {
          ref.removeEventListener('scroll', checkAndSetFades);
        }
      });
    };
  }, [paginatedBookings]);

  return (
    <Container>
      <Breadcrumb>
        <StyledBreadcrumbItem onClick={handleReturnToUsersList}>
          User Management
        </StyledBreadcrumbItem>
        <Breadcrumb.Item>{user?.fullName || 'User Details'}</Breadcrumb.Item>
      </Breadcrumb>
      {loading ? (
        <Spinner minHeight={200} fullScreen={true} />
      ) : (
        <StyledCard>
          <UserAvatar size={130} src={user?.profilePictureUrl}>
            {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
          </UserAvatar>

          <UserInfo>
            <UserNameContainer>
              <UserName>{user?.fullName || 'Unknown User'}</UserName>
              <Role>
                {user?.role ? getRoleNameById(user.role) : 'No Role Assigned'}
              </Role>
            </UserNameContainer>

            <EditContainer onClick={handleEditUser}>Edit</EditContainer>

            <ContactInfoContainer>
              <UserEmail>
                <StyledMail />
                {user?.email || 'No email provided'}
              </UserEmail>
              <UserPhoneNumber>
                <StyledPhone />
                {user?.phoneNumber || 'No phone number provided'}
              </UserPhoneNumber>
            </ContactInfoContainer>
          </UserInfo>
        </StyledCard>
      )}
      <BookingsCard>
        <BookingDetailsTitle>Booking Details</BookingDetailsTitle>
        <StyledTabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            { label: 'Upcoming', key: 'upcoming', disabled: bookingsLoading },
            { label: 'Completed', key: 'completed', disabled: bookingsLoading },
            { label: 'Cancelled', key: 'cancelled', disabled: bookingsLoading },
          ]}
        />
        {bookingsLoading && <Spinner minHeight={200} fullScreen={true} />}
        {!bookingsLoading && bookings.length === 0 && (
          <NoBookingMessage>
            {noBookingMessages[activeTab] || 'No bookings found'}
          </NoBookingMessage>
        )}
        {!bookingsLoading &&
          paginatedBookings.map((booking, index) => (
            <BookingCard key={index}>
              <CourtContainer>
                <CourtName>{booking.courtName}</CourtName>
                <Status>{activeTab}</Status>
              </CourtContainer>
              <SlotContainer>
                <SportNameWrapper>
                  <StyledDribbbleOutlined />
                  <SportName>{booking.sportName}</SportName>
                </SportNameWrapper>
                <SlotDateContainer>
                  <SlotTimingsContainer>
                    <StyledCalendarOutlined />
                    <SlotTime>
                      {booking.bookingSlots[0].startTime
                        ? getDate(booking.bookingSlots[0].startTime)
                        : ''}
                    </SlotTime>
                  </SlotTimingsContainer>
                  <SlotTimingsContainer>
                    <StyledClockCircleOutlined />
                    <SlotTime>
                      {getTime(booking.bookingSlots[0].startTime)}
                    </SlotTime>
                    -
                    <SlotTime>
                      {getTime(booking.bookingSlots[0].endTime)}
                    </SlotTime>
                  </SlotTimingsContainer>
                  <ActionText
                    onClick={() => handleOpenDrawer(booking.bookingId)}
                  >
                    View more
                  </ActionText>
                </SlotDateContainer>
              </SlotContainer>
              <BookingDateContainer>
                <Amount>Amount: Rs {booking.totalAmount}</Amount>
                <Dot></Dot>
                <BookingDate>
                  Booked On{' '}
                  {getDate(
                    typeof booking.bookingDate === 'string'
                      ? booking.bookingDate
                      : booking.bookingDate.toISOString(),
                  )}
                </BookingDate>
              </BookingDateContainer>
            </BookingCard>
          ))}
        {!bookingsLoading && (
          <StyledPagination
            current={currentPage}
            pageSize={BOOKINGS_PER_PAGE}
            total={bookings.length}
            onChange={handlePageChange}
          />
        )}
      </BookingsCard>
      {!loading && editModalVisible && (
        <UserForm
          open={editModalVisible}
          onClose={handleEditCancel}
          onSubmit={handleEditSubmit}
          initialValues={
            user
              ? {
                  ...user,
                  fullName: user.fullName ?? undefined,
                  email: user.email ?? undefined,
                  phoneNumber: user.phoneNumber ?? undefined,
                  roleId: user.role ?? undefined,
                  userRole: user.role ?? undefined,
                }
              : null
          }
        />
      )}
      {openDrawer && selectedBookingId && (
        <ViewBookingDrawer
          open={openDrawer}
          onClose={handleDrawerClose}
          bookingId={selectedBookingId}
          cancelSlot={false}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 8px;
  min-height: 100vh;
`;

const StyledCard = styled.div`
  width: 100%;
  max-width: 550px;
  padding: 14px;
  background: ${COLORS.white.cardBackground};
  border-radius: 8px;
  border: 1px solid ${COLORS.skyBlue.extraLight};
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const UserAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  padding: 16px;
`;

const UserNameContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const UserName = styled.p`
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  overflow-wrap: break-word;
  margin: 0px;
`;

const Role = styled.div`
  padding: 5px 12px;
  font-size: ${TEXT_SIZES.XS}px;
  background-color: ${COLORS.skyBlue.light};
  border-radius: 15px;
`;

const EditContainer = styled.div`
  color: ${COLORS.skyBlue.primary};
  margin-top: 8px;
  cursor: pointer;
`;

const ContactInfoContainer = styled.div`
  display: flex;
  gap: 8px;
  max-width: 320px;
  margin-top: 24px;
`;

const UserEmail = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${TEXT_SIZES.SM}px;
`;

const UserPhoneNumber = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  font-size: ${TEXT_SIZES.SM}px;
`;

const StyledMail = styled(MailOutlined)`
  background-color: ${COLORS.grey.lightMedium};
  padding: 4px;
  border-radius: 50%;
`;

const StyledPhone = styled(PhoneOutlined)`
  background-color: ${COLORS.grey.lightMedium};
  padding: 4px;
  border-radius: 50%;
`;

const BookingsCard = styled.div``;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    font-weight: ${FONT_WEIGHTS.MEDIUM};
  }
  .ant-tabs-tab-active {
    color: ${COLORS.skyBlue.primary}!important;
  }
  .ant-tabs-ink-bar {
    background-color: ${COLORS.skyBlue.primary};
  }
`;

const BookingDetailsTitle = styled.p`
  margin: 0px;
  font-size: ${TEXT_SIZES.LG}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-top: 40px;
`;

const BookingCard = styled(Card)`
  border: 1px solid ${COLORS.grey.secondaryLight};
  width: 100%;
  flex: 1;
  font-size: ${TEXT_SIZES.SM}px;
  margin-bottom: 12px;
`;

const CourtContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
`;

const CourtName = styled.p`
  margin: 0px;
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const Status = styled.div`
  padding: 4px 12px;
  background-color: ${COLORS.skyBlue.light};
  border-radius: 20px;
  color: ${COLORS.skyBlue.primary};
  font-size: ${TEXT_SIZES.XS}px;
`;

const SlotContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  align-items: center;
  flex: 1;
  margin-bottom: 8px;
`;

const SportName = styled.p`
  margin: 0px;
  flex: 1;
`;

const SlotDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 50px;
`;

const SlotTimingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  gap: 6px;
`;

const SlotTime = styled.p`
  margin: 0px;
`;

const BookingDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  color: ${COLORS.grey.medium};
  font-size: ${TEXT_SIZES.SM}px;
  gap: 6px;
`;

const Amount = styled.p`
  margin: 0px;
`;

const Dot = styled.div`
  width: 3px;
  height: 3px;
  background-color: ${COLORS.grey.medium};
  border-radius: 50%;
`;

const BookingDate = styled.p`
  margin: 0px;
`;

const SportNameWrapper = styled.div`
  display: flex;
  gap: 5px;
  flex: 1;
`;

const StyledDribbbleOutlined = styled(DribbbleOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledCalendarOutlined = styled(CalendarOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledClockCircleOutlined = styled(ClockCircleOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const NoBookingMessage = styled.p`
  font-size: ${TEXT_SIZES.MD}px;
  color: ${COLORS.grey.medium};
  text-align: center;
  margin-top: 20px;
`;

const StyledBreadcrumbItem = styled(Breadcrumb.Item)`
  &.ant-breadcrumb-link:hover {
    cursor: pointer;
    color: ${COLORS.skyBlue.primary};
  }
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 16;
`;

const ActionText = styled.span`
  color: ${COLORS.blue.medium};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${COLORS.blue.medium};
    text-decoration: underline;
  }
`;

export default UserDetailsView;
